import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { siteConfig, categories, articles } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory) || categories[0],
    [activeCategory],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory],
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: currentCategory.description,
    url: `${siteConfig.url}/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteConfig.name,
      url: siteConfig.url,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Header */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mt-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className={`${currentCategory.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{currentCategory.name}</h1>
                <p className="text-sm text-foreground-600 mt-1">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex items-center gap-1 py-3 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-200/70 hover:text-foreground-950'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article, idx) => {
                  const imageUrls = [
                    'https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20study%20desk%20with%20notebook%20and%20pen%20bathed%20in%20soft%20morning%20sunlight%2C%20potted%20plant%20nearby%2C%20clean%20aesthetic%2C%20peaceful%20creative%20atmosphere%2C%20editorial%20photography%20style%20with%20warm%20amber%20tones%20and%20gentle%20shadows&width=600&height=400&seq=article-01&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Colorful%20abstract%20paint%20splashes%20swirling%20in%20creative%20motion%20on%20white%20canvas%2C%20artistic%20explosion%20of%20warm%20coral%20and%20teal%20colors%2C%20inspiring%20creative%20energy%2C%20high%20detail%20editorial%20photography%20with%20soft%20lighting&width=600&height=400&seq=article-02&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Modern%20marketing%20strategy%20visualized%20through%20floating%20holographic%20data%20charts%20and%20content%20icons%20in%20warm%20amber%20and%20teal%2C%20clean%20minimalist%20aesthetic%2C%20futuristic%20yet%20warm%20atmosphere%2C%20editorial%20style%20with%20soft%20ambient%20light&width=600&height=400&seq=article-03&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Elegant%20academic%20library%20with%20wooden%20shelves%20filled%20with%20books%2C%20warm%20reading%20lamp%20glow%20on%20a%20polished%20desk%20with%20open%20notebook%2C%20scholarly%20atmosphere%2C%20refined%20minimalist%20aesthetic%2C%20amber%20and%20ivory%20tones%2C%20editorial%20photography&width=600&height=400&seq=article-04&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Professional%20business%20presentation%20scene%20with%20sleek%20laptop%20displaying%20charts%2C%20modern%20glass-walled%20conference%20room%2C%20warm%20ambient%20lighting%2C%20clean%20corporate%20aesthetic%2C%20amber%20and%20charcoal%20tones%2C%20editorial%20product%20photography&width=600&height=400&seq=article-05&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Vibrant%20social%20media%20content%20creation%20workspace%20with%20smartphone%20displaying%20colorful%20feed%2C%20creative%20desk%20setup%20with%20warm%20lighting%2C%20modern%20influencer%20aesthetic%2C%20coral%20and%20teal%20accents%2C%20editorial%20style%20with%20soft%20ambient%20glow&width=600&height=400&seq=article-06&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20with%20laptop%20showing%20productivity%20app%2C%20surrounded%20by%20minimalist%20stationery%20and%20warm%20desk%20lamp%2C%20organized%20creative%20workspace%2C%20amber%20and%20ivory%20tones%2C%20editorial%20product%20photography%20with%20soft%20natural%20lighting&width=600&height=400&seq=article-07&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Cozy%20reading%20nook%20with%20vintage%20typewriter%20and%20scattered%20manuscript%20pages%2C%20warm%20golden%20hour%20light%20streaming%20through%20window%2C%20nostalgic%20creative%20atmosphere%2C%20amber%20and%20sepia%20tones%2C%20artistic%20editorial%20photography%20with%20dreamy%20soft%20focus&width=600&height=400&seq=article-08&orientation=landscape',
                    'https://readdy.ai/api/search-image?query=Professional%20speaker%20at%20modern%20podium%20with%20warm%20stage%20lighting%2C%20engaged%20audience%20in%20soft%20focus%2C%20corporate%20event%20atmosphere%2C%20confident%20presentation%20style%2C%20amber%20and%20charcoal%20tones%2C%20editorial%20event%20photography&width=600&height=400&seq=article-09&orientation=landscape',
                  ];
                  const imageIndex = articles.findIndex((a) => a.slug === article.slug);
                  const imgUrl = imageUrls[imageIndex % imageUrls.length];

                  return (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden group cursor-pointer"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={imgUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100/80 text-accent-600 font-medium">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-500">{article.date}</span>
                        </div>
                        <h2 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-foreground-500 flex items-center gap-1">
                            <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                          </span>
                          <span className="text-sm text-primary-500 font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            阅读更多 <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无文章</h3>
                <p className="text-sm text-foreground-500">该分类下还没有文章，请查看其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}